import PageHero from "@/components/landing/PageHero";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";

const architecture = [
  { title: "One connected record model", body: "Pharmalify Spine connects Quality (eQMS), QC Lab (LIMS) and Asset Integrity (CMMS). Link deviations, CAPA, samples, results, equipment, maintenance and controlled documents so teams can follow evidence across operational domains." },
  { title: "Security and governance", body: "Role-based access, organization-level data isolation and traceable review workflows keep records and decisions accountable. Audit history and electronic-signature workflows support controlled operations; validation remains specific to your intended use and deployment." },
  { title: "Integration boundaries", body: "Plan interfaces around your existing ERP, MES and laboratory systems. Agree data ownership, record mappings, access boundaries and reconciliation requirements during implementation rather than treating disconnected copies as the source of truth." },
  { title: "Native intelligence, governed by Spine", body: "AI Agents use permitted context to propose and cite, never decide. Regulatory Watch connects regulatory updates to Change Control, Quality Risk Management (QRM) and the SOP lifecycle for human assessment and approval." },
];

export default function Platform() {
  useSEO({
    title: "Platform Architecture | Pharmalify Spine",
    description: "How Pharmalify Spine connects eQMS, LIMS and CMMS through linked records, security controls, integration boundaries and native governed intelligence.",
    canonical: "https://www.pharmalify.com/platform",
  });
  return (
    <main className="min-h-screen bg-background">
      <PageHero title="Platform architecture" description="How Pharmalify Spine is built: a unified GxP operations backbone with connected records, security and integration boundaries." />
      <section className="container py-16" aria-labelledby="architecture-heading">
        <h2 id="architecture-heading" className="text-3xl font-bold text-foreground mb-4">Three operating domains. One backbone.</h2>
        <p className="text-muted-foreground max-w-3xl mb-10">Built for small-to-mid biopharma teams, from emerging organizations to hundreds of users across multiple sites.</p>
        <div className="grid gap-6 md:grid-cols-2">
          {architecture.map(({ title, body }) => (
            <article key={title} className="rounded-xl border border-border bg-card p-8">
              <h3 className="text-xl font-semibold text-card-foreground mb-3">{title}</h3>
              <p className="text-muted-foreground leading-relaxed">{body}</p>
            </article>
          ))}
        </div>
      </section>
      <section className="bg-muted/30 py-16">
        <div className="container max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">Plan Spine around your operations</h2>
          <p className="text-muted-foreground mb-8">Review your record model, site boundaries and integration needs with us. Access is contract-based, with scope and onboarding agreed together.</p>
          <Button asChild><Link to="/contact">Request Walkthrough</Link></Button>
        </div>
      </section>
    </main>
  );
}
